import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { useDispatch, useStore } from "react-redux";
import { Button } from "react-bootstrap";
import {
  LuArrowLeft,
  LuBadgeCheck,
  LuCamera,
  LuChartColumn,
  LuCheck,
  LuDownload,
  LuFile,
  LuFolder,
  LuLoaderCircle,
  LuPalette,
  LuPencil,
  LuPipette,
  LuPlus,
  LuShapes,
  LuSmile,
  LuTrash2,
  LuType,
  LuUpload,
  LuX,
} from "react-icons/lu";
import AppModal from "../../common/modal/AppModal";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../common/utils";
import { createBox } from "../../../../common/common_tools";
import { addScenarioElementToCurrentScene } from "../../../../common/actions";
import { defaultForElement } from "../../../../plugins/InteractivePlugin/schema";
import { isClmAllowedPlugin } from "../../../../common/clm";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";
import { brandBoxId, topRightPosition } from "../../../utils/brandAssetPlacement";
import { brandFontCss, loadBrandFont } from "../../../utils/brandFonts";
import { SUPPORTED_GOOGLE_FONTS } from "../../../../common/font_extractor.es6";
import { applyBrandColor } from "../../../utils/brandColors";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import {
  addGoogleFont,
  createBrandCategory,
  createBrandColorSet,
  deleteBrandAsset,
  deleteBrandCategory,
  getBrandKit,
  getBrandPermissions,
  renameBrandCategory,
  renameBrandColorSet,
  setBrandColor,
  uploadBrandAsset,
} from "../../../services/brandKitService";
import ExternalDropzone from "../../external_provider/file_modal/APIProviders/common/ExternalDropzone";
import AssetGrid from "../elements_library/ui/AssetGrid";
import AssetCell from "../elements_library/ui/AssetCell";
import LazyAssetImage from "../elements_library/ui/LazyAssetImage";
import PanelSearch from "../elements_library/ui/PanelSearch";
import "../elements_library/panels/_panel.scss";
import "./_brandPanel.scss";

// The categories in the order the panel lists them (Canva's brand panel order).
const KINDS = [
  { kind: "logo", i18nKey: "BrandPanel.kind_logo", fallback: "Logos", Icon: LuBadgeCheck },
  { kind: "photo", i18nKey: "BrandPanel.kind_photo", fallback: "Photos", Icon: LuCamera },
  { kind: "graphic", i18nKey: "BrandPanel.kind_graphic", fallback: "Graphics", Icon: LuShapes },
  { kind: "icon", i18nKey: "BrandPanel.kind_icon", fallback: "Icons", Icon: LuSmile },
  { kind: "chart", i18nKey: "BrandPanel.kind_chart", fallback: "Charts", Icon: LuChartColumn },
  { kind: "font", i18nKey: "BrandPanel.kind_font", fallback: "Fonts", Icon: LuType },
];

// What the backend accepts per kind (brand-asset-file.ts) — narrows the picker.
const ACCEPT = {
  logo: "image/png,image/jpeg,image/webp,image/svg+xml,.svg",
  photo: "image/png,image/jpeg,image/webp",
  graphic: "image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg",
  icon: "image/png,image/jpeg,image/webp,image/svg+xml,.svg",
  chart: "image/png,image/jpeg,image/webp,image/svg+xml,.svg",
  font: ".woff2,.woff,.ttf,.otf",
  // The organization's own categories take what Graphics takes.
  custom: "image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg",
};

// Marks stay small in the corner; pictures may take more of the canvas.
const MAX_PCT = { logo: 15, icon: 15, photo: 30, graphic: 30, chart: 30, custom: 30 };

// The open category is a fixed kind ("logo", …) or one of the organization's
// own categories ("custom:<id>", kind `custom` on the server).
const CUSTOM = "custom:";
const NEW_CATEGORY = "new";
// The Colors card: the library's color sets (file-less `color` assets), each
// with the console's Color Palette slots; a swatch click applies one.
const COLORS = "colors";
const COLOR_SLOTS = [
  { role: "primary", i18nKey: "BrandPanel.color_primary", fallback: "Primary Color" },
  { role: "secondary", i18nKey: "BrandPanel.color_secondary", fallback: "Secondary Color" },
  { role: "accent", i18nKey: "BrandPanel.color_accent", fallback: "Accent Color" },
];
// A picked color is saved once the picker settles, not on every drag step.
const PICKER_SAVE_DELAY = 400;

// "#40CFDE" / "40cfde" / "#4cd" → "#40cfde"; anything else → null. The server
// normalizes the same way.
const normalizeHex = (value) => {
  let v = String(value || "")
    .trim()
    .replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(v)) v = v.replace(/./g, (c) => c + c);
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : null;
};
const CATEGORY_NAME_MAX = 60;
const IMAGE_PLUGIN = "HotspotImages";
const TEXT_PLUGIN = "BasicText"; // shown as "Rich Text"

// Case-insensitive match on the asset name. I/ı/İ/i fold to one letter so
// "inter" finds "Inter" in both Turkish and English casing.
const fold = (text) => (text || "").toLocaleLowerCase("tr").replace(/ı/g, "i");
const matches = (asset, term) => fold(asset.name).includes(fold(term));

const tr = (key, fallback, opts) => {
  const value = i18n.t(key, opts);
  return value && value !== key ? value : fallback;
};

const sizeLabel = (bytes) => {
  const kb = Math.max(1, Math.round((bytes || 0) / 1024));
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
};

// Blob previews for the queue; jsdom (tests) has no createObjectURL.
const previewOf = (file) =>
  file.type && file.type.startsWith("image/") && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(file)
    : null;
const dropPreview = (row) => {
  if (row.preview && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(row.preview);
};

let queueSeq = 0;

const BrandPanel = ({
  isClm,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  boxes,
  pluginToolbars,
  onBoxAdded,
  onClose,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [kit, setKit] = useState(null);
  // Per color slot ("<setId>:<role>"): what is typed / picked but not stored yet,
  // and the last error.
  const [colorDrafts, setColorDrafts] = useState({});
  const [colorErrors, setColorErrors] = useState({});
  const colorSaving = useRef(new Set());
  const pickerTimers = useRef({});
  const [addingColorSet, setAddingColorSet] = useState(false);
  const [colorSetError, setColorSetError] = useState(null);
  // The color set being renamed in place: { id, name, saving, error }.
  const [renamingSet, setRenamingSet] = useState(null);
  // What the last color click went to: text | shape | background | unsupported.
  const [colorResult, setColorResult] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [perms, setPerms] = useState({ canUpload: false, canManage: false });
  const [error, setError] = useState(null);
  // A Google font being added from the Fonts folder's picker.
  const [addingFont, setAddingFont] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const term = searchTerm.trim();
  // Files dropped into the open category wait here until "Upload" — like the
  // Upload Files tab. A row: { id, file, preview, error }.
  const [queue, setQueue] = useState([]);
  // The queued logo to make the organization's main logo (owner/admin only).
  const [primaryRowId, setPrimaryRowId] = useState(null);
  // { kind, done, total } while the queue uploads.
  const [uploading, setUploading] = useState(null);
  // The asset shown in the preview dialog / waiting for delete confirmation.
  // Never both at once (no popup over a popup).
  const [previewAsset, setPreviewAsset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // The organization category being created (id NEW_CATEGORY) or renamed, in
  // place of its card: { id, name, saving, error }.
  const [editing, setEditing] = useState(null);
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState(null);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  useEffect(() => () => queueRef.current.forEach(dropPreview), []);
  useEffect(() => () => Object.values(pickerTimers.current).forEach(clearTimeout), []);

  // The organization's brand library — one per organization, no brand profile involved.
  const loadKit = useCallback(() => {
    setError(null);
    return getBrandKit()
      .then(setKit)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getBrandPermissions().then((p) => !cancelled && setPerms(p));
    loadKit();
    return () => {
      cancelled = true;
    };
  }, [loadKit]);

  // So each font's "Aa" preview renders in the font itself.
  useEffect(() => {
    if (openKey === "font" && kit && kit.assets) (kit.assets.font || []).forEach(loadBrandFont);
  }, [openKey, kit]);

  const categories = (kit && kit.categories) || [];

  // What the panel needs to show / upload into a category, fixed or the organization's own.
  const categoryOf = (key) => {
    if (!key || !kit) return null;
    if (key.startsWith(CUSTOM)) {
      const id = key.slice(CUSTOM.length);
      const category = categories.find((c) => c.id === id);
      if (!category) return null;
      return {
        key,
        kind: "custom",
        categoryId: id,
        testKey: `custom-${id}`,
        title: category.name,
        assets: (kit.categoryAssets && kit.categoryAssets[id]) || [],
      };
    }
    const fixed = KINDS.find((k) => k.kind === key);
    return {
      key,
      kind: key,
      categoryId: null,
      testKey: key,
      title: tr(fixed.i18nKey, fixed.fallback),
      assets: (kit.assets && kit.assets[key]) || [],
    };
  };

  const queueFiles = (picked) => {
    const files = (Array.isArray(picked) ? picked : [picked]).filter(Boolean);
    if (!files.length || uploading) return;
    setQueue((q) => [
      ...q,
      ...files.map((file) => ({
        id: `q${++queueSeq}`,
        file,
        preview: previewOf(file),
        error: null,
      })),
    ]);
  };

  const removeRow = (id) => {
    queue.filter((r) => r.id === id).forEach(dropPreview);
    setQueue(queue.filter((r) => r.id !== id));
    if (primaryRowId === id) setPrimaryRowId(null);
  };

  const clearQueue = () => {
    queue.forEach(dropPreview);
    setQueue([]);
    setPrimaryRowId(null);
  };

  // The queue belongs to the open category.
  const switchKind = (key) => {
    clearQueue();
    setOpenKey(key);
  };

  // Queued files upload one after another; one bad file does not stop the
  // rest — it stays queued with its error — and the library reloads once.
  const uploadQueue = async () => {
    const open = categoryOf(openKey);
    if (!open || !queue.length || uploading) return;
    setError(null);
    const failed = [];
    for (let i = 0; i < queue.length; i++) {
      const row = queue[i];
      setUploading({ key: open.key, done: i, total: queue.length });
      try {
        await uploadBrandAsset(open.kind, row.file, {
          isPrimary: row.id === primaryRowId,
          ...(open.categoryId ? { categoryId: open.categoryId } : {}),
        });
        dropPreview(row);
      } catch (e) {
        failed.push({ ...row, error: e.message });
      }
    }
    setUploading(null);
    setQueue(failed);
    if (!failed.some((r) => r.id === primaryRowId)) setPrimaryRowId(null);
    await loadKit();
  };

  // The Fonts folder's picker: a family from the editor's Google Fonts list
  // joins the library without a file (the Visor and exports load it too).
  const pickGoogleFont = async (family) => {
    if (!family || addingFont) return;
    setError(null);
    setAddingFont(true);
    try {
      await addGoogleFont(family);
      await loadKit();
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingFont(false);
    }
  };

  const renderGoogleFontPicker = (fonts) => {
    const have = new Set(fonts.map((f) => (f.metadata && f.metadata.family) || f.name));
    const choices = SUPPORTED_GOOGLE_FONTS.filter((family) => !have.has(family));
    return (
      <select
        className="brand-panel__font-select"
        data-testid="brand-google-font-select"
        value=""
        disabled={addingFont}
        onChange={(e) => pickGoogleFont(e.target.value)}
      >
        <option value="">
          {addingFont
            ? tr("BrandPanel.font_adding", "Adding…")
            : tr("BrandPanel.font_pick", "Pick a Google font…")}
        </option>
        {choices.map((family) => (
          <option key={family} value={family}>
            {family}
          </option>
        ))}
      </select>
    );
  };

  const downloadAsset = (asset) => asset.url && window.open(asset.url, "_blank");

  // ---- the organization's own categories (owner/admin) ----

  const startEditing = (id, name = "") => setEditing({ id, name, saving: false, error: null });

  const saveCategory = async () => {
    if (!editing || editing.saving) return;
    const name = editing.name.trim();
    if (!name) return;
    const current = categories.find((c) => c.id === editing.id);
    if (current && current.name === name) return setEditing(null);
    setEditing({ ...editing, saving: true, error: null });
    try {
      if (editing.id === NEW_CATEGORY) await createBrandCategory(name);
      else await renameBrandCategory(editing.id, name);
      setEditing(null);
      await loadKit();
    } catch (e) {
      setEditing((ed) => ed && { ...ed, saving: false, error: e.message });
    }
  };

  const removeCategory = async () => {
    const category = confirmDeleteCategory;
    if (!category || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteBrandCategory(category.id);
      if (openKey === CUSTOM + category.id) switchKind(null);
      await loadKit();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setConfirmDeleteCategory(null);
    }
  };

  const removeAsset = async () => {
    const asset = confirmDelete;
    if (!asset || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteBrandAsset(asset.id);
      await loadKit();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  // Top-right of the current scene, visible for the whole scene. The box id
  // carries the asset (brandBoxId), which is what lets the canvas right-click
  // menu push its position onto every other scene (BoxContextMenu).
  // A font adds a Rich Text box with its usual placeholder text, set in that
  // font (textFontFamily — the same field the pill's font picker writes).
  // Editor only: the Visor and exports load Google Fonts only (brandFonts.js).
  const placeFont = async (asset) => {
    if (!asset.url || !onBoxAdded || !IdealStudio.Plugins.get(TEXT_PLUGIN)) return;
    if (isClm && !isClmAllowedPlugin(TEXT_PLUGIN)) return;
    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const navItem = cv ? containedViews[containedViewSelected] : navItems[navItemSelected];
    if (!navItem) return;

    await loadBrandFont(asset);
    const inASlide = isSlide(navItem.type);
    createBox(
      {
        parent: inASlide ? navItem.id : navItem.boxes && navItem.boxes[0],
        container: 0,
        name: TEXT_PLUGIN,
        position: {
          x: randomPositionGenerator(10, 30),
          y: randomPositionGenerator(10, 30),
          type: "absolute",
        },
        id: ID_PREFIX_BOX + Date.now(),
        page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
        initialState: { textFontFamily: brandFontCss(asset) },
      },
      TEXT_PLUGIN,
      inASlide,
      onBoxAdded,
      boxes
    );
  };

  const placeOnCurrentScene = async (asset) => {
    if (asset.kind === "font") return placeFont(asset);
    if (!asset.url || !onBoxAdded) return;
    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const navItem = cv ? containedViews[containedViewSelected] : navItems[navItemSelected];
    if (!navItem) return;

    const interactiveBoxId = !cv
      ? (navItem.boxes || []).find((id) => pluginToolbars?.[id]?.pluginId === "InteractivePlugin")
      : null;
    if (interactiveBoxId) {
      // Interactive scenes take images as scenario elements (as My Files does);
      // those have no free position, so there is nothing to copy to other scenes.
      dispatch(
        addScenarioElementToCurrentScene(interactiveBoxId, {
          ...defaultForElement("visualimage"),
          imageUrl: asset.url,
        })
      );
      return;
    }

    if (!IdealStudio.Plugins.get(IMAGE_PLUGIN)) return;
    if (isClm && !isClmAllowedPlugin(IMAGE_PLUGIN)) return;

    const inASlide = isSlide(navItem.type);
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];
    const page = cv ? containedViewSelected : navItem.id || navItemSelected || 0;
    const size = await getMediaBoxSize(asset.url, "image", { maxPct: MAX_PCT[asset.kind] || 30 });
    const id = brandBoxId(asset.id, page);

    createBox(
      {
        parent,
        container: 0,
        name: IMAGE_PLUGIN,
        componentType: "Brand",
        position: topRightPosition(size),
        id,
        page,
        url: asset.url,
        width: size.width,
        height: size.height,
        widthUnit: size.widthUnit,
        heightUnit: size.heightUnit,
        timeline: { startFrame: 0, visible: true },
      },
      IMAGE_PLUGIN,
      inASlide,
      onBoxAdded,
      boxes
    );
  };

  const countLabel = (count) => `${count ?? 0} ${i18n.t("files") || "dosya"}`;

  // Creating or renaming an organization category happens in place of its card.
  const renderNameEditor = () => (
    <form
      className="brand-panel__category brand-panel__category--editing"
      data-testid="brand-category-editor"
      onSubmit={(e) => {
        e.preventDefault();
        saveCategory();
      }}
    >
      <span className="brand-panel__category-icon">
        <LuFolder size={17} />
      </span>
      <input
        className="brand-panel__category-input"
        data-testid="brand-category-name"
        autoFocus
        value={editing.name}
        maxLength={CATEGORY_NAME_MAX}
        disabled={editing.saving}
        placeholder={tr("BrandPanel.category_name_placeholder", "Category name")}
        aria-label={tr("BrandPanel.category_name_placeholder", "Category name")}
        onChange={(e) => setEditing({ ...editing, name: e.target.value, error: null })}
        onKeyDown={(e) => e.key === "Escape" && setEditing(null)}
        onBlur={() => !editing.name.trim() && !editing.saving && setEditing(null)}
      />
      {editing.error ? (
        <span className="brand-panel__category-error" data-testid="brand-category-error">
          {editing.error}
        </span>
      ) : null}
      <span className="brand-panel__category-edit-actions">
        <button
          type="button"
          className="brand-panel__icon-btn"
          data-testid="brand-category-cancel"
          title={i18n.t("cancel") || "Vazgeç"}
          disabled={editing.saving}
          // Keeps the input from blurring (and cancelling) before the click lands.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setEditing(null)}
        >
          <LuX size={14} />
        </button>
        <button
          type="submit"
          className="brand-panel__icon-btn brand-panel__icon-btn--primary"
          data-testid="brand-category-save"
          title={i18n.t("save") || "Kaydet"}
          disabled={!editing.name.trim() || editing.saving}
          onMouseDown={(e) => e.preventDefault()}
        >
          {editing.saving ? <LuLoaderCircle size={14} /> : <LuCheck size={14} />}
        </button>
      </span>
    </form>
  );

  const renderCustomCategory = (category) => {
    if (editing && editing.id === category.id) {
      return <React.Fragment key={category.id}>{renderNameEditor()}</React.Fragment>;
    }
    return (
      <div key={category.id} className="brand-panel__category-wrap">
        <button
          type="button"
          className="brand-panel__category"
          data-testid={`brand-category-custom-${category.id}`}
          onClick={() => switchKind(CUSTOM + category.id)}
        >
          <span className="brand-panel__category-icon">
            <LuFolder size={17} />
          </span>
          <span className="brand-panel__category-text">
            <span className="brand-panel__category-name" title={category.name}>
              {category.name}
            </span>
            <span className="brand-panel__category-count">{countLabel(category.assetCount)}</span>
          </span>
        </button>
        {/* Renaming / deleting a category is an owner/admin action (BrandWriteGuard). */}
        {perms.canManage ? (
          <span className="brand-panel__category-actions">
            <button
              type="button"
              className="brand-panel__icon-btn"
              data-testid={`brand-category-rename-${category.id}`}
              title={tr("BrandPanel.rename", "Rename")}
              onClick={() => startEditing(category.id, category.name)}
            >
              <LuPencil size={13} />
            </button>
            <button
              type="button"
              className="brand-panel__icon-btn brand-panel__icon-btn--danger"
              data-testid={`brand-category-delete-${category.id}`}
              title={tr("BrandPanel.delete_category", "Delete category")}
              onClick={() => setConfirmDeleteCategory(category)}
            >
              <LuTrash2 size={13} />
            </button>
          </span>
        ) : null}
      </div>
    );
  };

  // [{ id, name, primary, secondary, accent }] — a slot is "#rrggbb" or null.
  const colorSets = (kit && kit.colors) || [];
  const slotKey = (set, role) => `${set.id}:${role}`;

  const setDraft = (key, value) => setColorDrafts((d) => ({ ...d, [key]: value }));
  const dropDraft = (key) =>
    setColorDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  const setColorError = (key, message) => setColorErrors((e) => ({ ...e, [key]: message }));
  const storeColorSet = (stored) =>
    setKit((k) =>
      k ? { ...k, colors: (k.colors || []).map((c) => (c.id === stored.id ? stored : c)) } : k
    );

  // Stores a slot's typed / picked value (null empties it). The stored color
  // again → nothing to do; not a color or refused → the stored one comes back.
  const commitColor = async (set, role, value) => {
    const key = slotKey(set, role);
    clearTimeout(pickerTimers.current[key]);
    if (colorSaving.current.has(key)) return;
    const saved = set[role] || "";
    const raw = value === null ? "" : String(value || "").trim();
    const hex = raw ? normalizeHex(raw) : null;
    if ((!raw && value !== null) || (hex || "") === saved) {
      dropDraft(key);
      return;
    }
    if (raw && !hex) {
      dropDraft(key);
      setColorError(key, tr("BrandPanel.color_invalid", "Enter a hex color like #40cfde"));
      return;
    }
    colorSaving.current.add(key);
    setColorError(key, null);
    try {
      storeColorSet(await setBrandColor(set.id, role, hex));
    } catch (err) {
      setColorError(key, err.message);
    } finally {
      colorSaving.current.delete(key);
      dropDraft(key);
    }
  };

  const pickColor = (set, role, value) => {
    const key = slotKey(set, role);
    setDraft(key, value);
    setColorError(key, null);
    clearTimeout(pickerTimers.current[key]);
    pickerTimers.current[key] = setTimeout(() => commitColor(set, role, value), PICKER_SAVE_DELAY);
  };

  const addColorSet = async () => {
    if (addingColorSet) return;
    setAddingColorSet(true);
    setColorSetError(null);
    const n = colorSets.length + 1;
    try {
      const created = await createBrandColorSet(
        tr("BrandPanel.color_set_default", `Color set ${n}`, { n })
      );
      setKit((k) => (k ? { ...k, colors: [...(k.colors || []), created] } : k));
      // Ready to name it.
      setRenamingSet({ id: created.id, name: created.name, saving: false, error: null });
    } catch (err) {
      setColorSetError(err.message);
    } finally {
      setAddingColorSet(false);
    }
  };

  // Empty or unchanged → the old name stays; a refusal keeps the field open.
  const saveSetName = async () => {
    const editing = renamingSet;
    if (!editing || editing.saving) return;
    const set = colorSets.find((c) => c.id === editing.id);
    const name = editing.name.trim();
    if (!set || !name || name === set.name) {
      setRenamingSet(null);
      return;
    }
    setRenamingSet({ ...editing, saving: true, error: null });
    try {
      storeColorSet(await renameBrandColorSet(set.id, name));
      setRenamingSet(null);
    } catch (err) {
      setRenamingSet({ ...editing, saving: false, error: err.message });
    }
  };

  const COLOR_RESULT_TEXT = {
    text: ["BrandPanel.color_applied_text", "Applied to the text"],
    shape: ["BrandPanel.color_applied_shape", "Applied to the shape"],
    background: ["BrandPanel.color_applied_background", "Applied to the scene background"],
    unsupported: ["BrandPanel.color_unsupported", "This element can't take a color"],
  };

  const applyColor = (hex) => setColorResult(applyBrandColor(dispatch, store.getState, hex));

  const renderColors = () => (
    <>
      <div className="brand-panel__category-head">
        <button
          type="button"
          className="brand-panel__back"
          data-testid="brand-back"
          title={tr("BrandPanel.back", "Back")}
          onClick={() => switchKind(null)}
        >
          <LuArrowLeft size={14} />
        </button>
        <span className="brand-panel__title">{tr("BrandPanel.colors", "Colors")}</span>
      </div>
      <div className="brand-panel__hint">
        {tr(
          "BrandPanel.colors_hint",
          "Click a color: it goes to the selected text or shape — with nothing selected, to the scene background."
        )}
      </div>
      {colorResult ? (
        <div
          className={`brand-panel__color-result${colorResult === "unsupported" ? " is-warning" : ""}`}
          data-testid="brand-color-result"
          data-result={colorResult}
        >
          {tr(...COLOR_RESULT_TEXT[colorResult])}
        </div>
      ) : null}
      {!kit ? <LoadingRow /> : null}
      {kit && !colorSets.length ? (
        <div className="brand-panel__empty-kind" data-testid="brand-colors-empty">
          {tr("BrandPanel.colors_empty", "No brand colors yet")}
        </div>
      ) : null}
      {colorSets.map((set) => (
        <div
          key={set.id}
          className="brand-panel__color-set"
          data-testid={`brand-color-set-${set.id}`}
        >
          <div className="brand-panel__color-set-head">
            {renamingSet && renamingSet.id === set.id ? (
              <input
                type="text"
                className="brand-panel__color-set-input"
                data-testid="brand-color-set-name"
                autoFocus
                maxLength={CATEGORY_NAME_MAX}
                value={renamingSet.name}
                disabled={renamingSet.saving}
                aria-label={tr("BrandPanel.color_set_name", "Color set name")}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setRenamingSet({ ...renamingSet, name: e.target.value, error: null })
                }
                onBlur={saveSetName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveSetName();
                  if (e.key === "Escape") setRenamingSet(null);
                }}
              />
            ) : (
              <span className="brand-panel__color-set-name" title={set.name}>
                {set.name}
              </span>
            )}
            {/* Renaming is open to anyone who may edit the colors (like the server's rule). */}
            {perms.canUpload && !(renamingSet && renamingSet.id === set.id) ? (
              <button
                type="button"
                className="brand-panel__icon-btn"
                data-testid={`brand-color-set-rename-${set.id}`}
                title={tr("BrandPanel.rename", "Rename")}
                onClick={() =>
                  setRenamingSet({ id: set.id, name: set.name, saving: false, error: null })
                }
              >
                <LuPencil size={13} />
              </button>
            ) : null}
            {/* Deleting a set stays owner/admin (BrandWriteGuard), like any asset. */}
            {perms.canManage ? (
              <button
                type="button"
                className="brand-panel__icon-btn brand-panel__icon-btn--danger"
                data-testid={`brand-color-set-delete-${set.id}`}
                title={tr("BrandPanel.delete_color_set", "Delete color set")}
                onClick={() => setConfirmDelete({ ...set, isColorSet: true })}
              >
                <LuTrash2 size={13} />
              </button>
            ) : null}
          </div>
          {renamingSet && renamingSet.id === set.id && renamingSet.error ? (
            <div className="brand-panel__category-error" data-testid="brand-color-set-name-error">
              {renamingSet.error}
            </div>
          ) : null}
          {COLOR_SLOTS.map(({ role, i18nKey, fallback }) => {
            const key = slotKey(set, role);
            const label = tr(i18nKey, fallback);
            const saved = set[role] || "";
            const value = colorDrafts[key] !== undefined ? colorDrafts[key] : saved;
            const shown = normalizeHex(value) || saved;
            return (
              <div key={role} className="brand-panel__color-slot">
                <span className="brand-panel__color-label">{label}</span>
                <div className="brand-panel__color-row">
                  {/* A filled swatch applies its color; an empty one opens the picker. */}
                  <button
                    type="button"
                    className={`brand-panel__color${saved ? "" : " is-empty"}`}
                    data-testid={`brand-color-apply-${role}`}
                    title={
                      saved
                        ? `${tr("BrandPanel.color_apply", "Apply")} ${saved}`
                        : tr("BrandPanel.color_pick", "Pick a color")
                    }
                    disabled={!saved && !perms.canUpload}
                    style={saved ? { background: saved } : undefined}
                    onClick={(e) =>
                      saved
                        ? applyColor(saved)
                        : e.currentTarget.parentElement.querySelector('input[type="color"]').click()
                    }
                  >
                    {!saved && perms.canUpload ? <LuPlus size={14} /> : null}
                  </button>
                  <input
                    type="text"
                    className="brand-panel__color-hex"
                    data-testid={`brand-color-hex-${role}`}
                    placeholder={tr("BrandPanel.color_empty", "Empty")}
                    maxLength={7}
                    spellCheck={false}
                    readOnly={!perms.canUpload}
                    aria-label={`${label} — ${tr("BrandPanel.color_hex", "Hex code")}`}
                    value={value}
                    onChange={(e) => {
                      setDraft(key, e.target.value);
                      setColorError(key, null);
                    }}
                    onBlur={(e) => perms.canUpload && commitColor(set, role, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && perms.canUpload)
                        commitColor(set, role, e.target.value);
                      if (e.key === "Escape") dropDraft(key);
                    }}
                  />
                  {/* Like the console's Color Palette: the hex with its picker. */}
                  {perms.canUpload ? (
                    <label
                      className="brand-panel__icon-btn brand-panel__color-edit"
                      title={tr("BrandPanel.color_pick", "Pick a color")}
                    >
                      <LuPipette size={13} />
                      <input
                        type="color"
                        className="brand-panel__color-picker"
                        data-testid={`brand-color-picker-${role}`}
                        aria-label={`${label} — ${tr("BrandPanel.color_pick", "Pick a color")}`}
                        value={shown || "#000000"}
                        onChange={(e) => pickColor(set, role, e.target.value)}
                      />
                    </label>
                  ) : null}
                  {perms.canUpload && saved ? (
                    <button
                      type="button"
                      className="brand-panel__icon-btn"
                      data-testid={`brand-color-clear-${role}`}
                      title={tr("BrandPanel.color_clear", "Remove color")}
                      onClick={() => commitColor(set, role, null)}
                    >
                      <LuX size={13} />
                    </button>
                  ) : null}
                </div>
                {colorErrors[key] ? (
                  <div
                    className="brand-panel__category-error"
                    data-testid={`brand-color-error-${role}`}
                  >
                    {colorErrors[key]}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
      {colorSetError ? (
        <div className="brand-panel__category-error" data-testid="brand-color-set-error">
          {colorSetError}
        </div>
      ) : null}
      {kit && perms.canUpload ? (
        <button
          type="button"
          className="brand-panel__color-set-new"
          data-testid="brand-color-set-new"
          disabled={addingColorSet}
          onClick={addColorSet}
        >
          {addingColorSet ? <LuLoaderCircle size={14} /> : <LuPlus size={14} />}
          {tr("BrandPanel.color_set_new", "New color set")}
        </button>
      ) : null}
    </>
  );

  const renderCategories = () => (
    <div className="brand-panel__categories">
      {KINDS.map(({ kind, i18nKey, fallback, Icon }) => (
        <button
          key={kind}
          type="button"
          className="brand-panel__category"
          data-testid={`brand-category-${kind}`}
          onClick={() => switchKind(kind)}
        >
          <span className="brand-panel__category-icon">
            <Icon size={17} />
          </span>
          <span className="brand-panel__category-text">
            <span className="brand-panel__category-name">{tr(i18nKey, fallback)}</span>
            <span className="brand-panel__category-count">
              {countLabel(kit?.assetCounts?.[kind])}
            </span>
          </span>
        </button>
      ))}
      <button
        type="button"
        className="brand-panel__category"
        data-testid="brand-category-colors"
        onClick={() => {
          setColorResult(null);
          setColorErrors({});
          switchKind(COLORS);
        }}
      >
        <span className="brand-panel__category-icon">
          <LuPalette size={17} />
        </span>
        <span className="brand-panel__category-text">
          <span className="brand-panel__category-name">{tr("BrandPanel.colors", "Colors")}</span>
          <span className="brand-panel__category-count">
            {kit ? colorSets.length : "…"} {tr("BrandPanel.color_sets_unit", "color sets")}
          </span>
        </span>
      </button>
      {categories.map(renderCustomCategory)}
      {/* Canva's "+ New category" tile — the organization's own categories (owner/admin). */}
      {perms.canManage ? (
        editing && editing.id === NEW_CATEGORY ? (
          renderNameEditor()
        ) : (
          <button
            type="button"
            className="brand-panel__category brand-panel__category--new"
            data-testid="brand-category-new"
            onClick={() => startEditing(NEW_CATEGORY)}
          >
            <span className="brand-panel__category-icon brand-panel__category-icon--new">
              <LuPlus size={17} />
            </span>
            <span className="brand-panel__category-text">
              <span className="brand-panel__category-name">
                {tr("BrandPanel.new_category", "New category")}
              </span>
            </span>
          </button>
        )
      ) : null}
    </div>
  );

  const renderAsset = (asset) => {
    const isFont = asset.kind === "font";
    return (
      <AssetCell
        key={asset.id}
        dragProps={{ "data-testid": `brand-asset-${asset.id}` }}
        title={asset.name}
        label={asset.name}
        onAdd={() => placeOnCurrentScene(asset)}
        previewLabel={i18n.t("preview") || "Önizle"}
        onPreview={asset.url ? () => setPreviewAsset(asset) : undefined}
        actions={[
          {
            key: "download",
            icon: <LuDownload size={14} />,
            title: i18n.t("download") || "İndir",
            onClick: () => downloadAsset(asset),
          },
          // Deleting is an owner/admin action (BrandWriteGuard on the server).
          ...(perms.canManage
            ? [
                {
                  key: "delete",
                  icon: <LuTrash2 size={14} />,
                  title: i18n.t("delete") || "Sil",
                  danger: true,
                  onClick: () => setConfirmDelete(asset),
                },
              ]
            : []),
        ]}
        badge={
          asset.isPrimary ? (
            <span className="brand-panel__badge" data-testid="brand-primary-badge">
              {tr("BrandPanel.primary", "Primary")}
            </span>
          ) : null
        }
      >
        {isFont ? (
          <span className="brand-panel__font" style={{ fontFamily: brandFontCss(asset) }}>
            Aa
          </span>
        ) : (
          <LazyAssetImage src={asset.url} alt={asset.name} />
        )}
      </AssetCell>
    );
  };

  const renderQueue = (busy) => {
    // Choosing the main logo is an owner/admin decision (the server enforces it too).
    const choosePrimary = openKey === "logo" && perms.canManage;
    return (
      <div className="brand-panel__queue" data-testid="brand-upload-queue">
        <div className="uploadQueue">
          {queue.map((row) => (
            <div
              key={row.id}
              className={`uploadQueue-row${row.error ? " error" : ""}`}
              data-testid="brand-queue-row"
            >
              <span
                className="uploadQueue-thumb"
                style={row.preview ? { backgroundImage: `url(${row.preview})` } : undefined}
              >
                {row.preview ? null : <LuFile size={18} />}
              </span>
              <span className="uploadQueue-info">
                <span className="uploadQueue-name" title={row.file.name}>
                  {row.file.name}
                </span>
                <span className="uploadQueue-meta">{sizeLabel(row.file.size)}</span>
                {choosePrimary ? (
                  <label className="brand-panel__primary-check">
                    <input
                      type="checkbox"
                      checked={primaryRowId === row.id}
                      disabled={busy}
                      data-testid="brand-queue-primary"
                      onChange={(e) => setPrimaryRowId(e.target.checked ? row.id : null)}
                    />
                    {tr("BrandPanel.make_primary", "Set as main logo")}
                  </label>
                ) : null}
                {row.error ? <span className="brand-panel__queue-error">{row.error}</span> : null}
              </span>
              <button
                type="button"
                className="uploadQueue-remove"
                disabled={busy}
                title={tr("FileModal.APIProviders.clear", "Clear")}
                data-testid="brand-queue-remove"
                onClick={() => removeRow(row.id)}
              >
                <LuX size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="brand-panel__queue-actions">
          <Button
            variant="primary"
            disabled={busy}
            data-testid="brand-upload-start"
            onClick={uploadQueue}
          >
            <LuUpload size={14} /> {tr("FileModal.APIProviders.upload", "Upload")}
            {queue.length > 1 ? ` (${queue.length})` : ""}
          </Button>
          <Button
            variant="outline-secondary"
            disabled={busy}
            data-testid="brand-upload-clear"
            onClick={clearQueue}
          >
            <LuX size={14} /> {tr("FileModal.APIProviders.clear", "Clear")}
          </Button>
        </div>
        {busy ? (
          <div className="brand-panel__uploading">
            <LuLoaderCircle size={12} />
            {tr("BrandPanel.uploading", "Uploading")} {uploading.done + 1}/{uploading.total}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCategory = (open) => {
    const all = open.assets;
    const items = term ? all.filter((a) => matches(a, term)) : all;
    const busy = uploading && uploading.key === open.key;
    return (
      <>
        <div className="brand-panel__category-head">
          <button
            type="button"
            className="brand-panel__back"
            data-testid="brand-back"
            title={tr("BrandPanel.back", "Back")}
            onClick={() => switchKind(null)}
          >
            <LuArrowLeft size={14} />
          </button>
          <span className="brand-panel__title">{open.title}</span>
        </div>
        {perms.canUpload && open.kind === "font" ? renderGoogleFontPicker(all) : null}
        {perms.canUpload ? (
          <div
            className={`brand-panel__dropzone${busy ? " is-busy" : ""}`}
            data-testid={`brand-upload-${open.testKey}`}
          >
            {/* Same drop/click target and queue as the Upload Files tab. */}
            <ExternalDropzone accept={ACCEPT[open.kind]} multiple callback={queueFiles} />
            {queue.length ? renderQueue(busy) : null}
          </div>
        ) : null}
        {open.kind !== "font" ? (
          <div className="brand-panel__hint" data-testid="brand-rightclick-hint">
            {tr(
              "BrandPanel.rightclick_hint",
              "Click to add to this scene. Right-click it on the canvas → “Update position on all scenes” to use the same spot everywhere."
            )}
          </div>
        ) : null}
        {items.length ? (
          <AssetGrid columns={3} cellSize={120} gap={10}>
            {items.map(renderAsset)}
          </AssetGrid>
        ) : (
          <div className="brand-panel__empty-kind">
            {term
              ? tr("BrandPanel.no_results", "No results")
              : tr("BrandPanel.empty_kind", "Empty")}
          </div>
        )}
      </>
    );
  };

  // Searching from the categories home looks through every category at once.
  const renderSearchResults = () => {
    const items = [
      ...KINDS.flatMap(({ kind }) => (kit.assets && kit.assets[kind]) || []),
      ...categories.flatMap((c) => (kit.categoryAssets && kit.categoryAssets[c.id]) || []),
    ].filter((a) => matches(a, term));
    return items.length ? (
      <AssetGrid columns={3} cellSize={120} gap={10}>
        {items.map(renderAsset)}
      </AssetGrid>
    ) : (
      <div className="brand-panel__empty-kind" data-testid="brand-search-empty">
        {tr("BrandPanel.no_results", "No results")}
      </div>
    );
  };

  const renderPreview = () => (
    <AppModal
      show
      onClose={() => setPreviewAsset(null)}
      title={previewAsset.name}
      centered
      size="lg"
      showSceneToggle={false}
      className="libraryPreviewModal"
    >
      {previewAsset.kind === "font" ? (
        <div
          className="brand-panel__font-preview"
          data-testid="brand-preview-font"
          style={{ fontFamily: brandFontCss(previewAsset) }}
        >
          <span className="brand-panel__font-preview-big">Aa</span>
          <span>ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ</span>
          <span>abcçdefgğhıijklmnoöprsştuüvyz</span>
          <span>0123456789</span>
        </div>
      ) : (
        <img
          src={previewAsset.url}
          alt={previewAsset.name}
          data-testid="brand-preview-image"
          className="brand-panel__image-preview"
        />
      )}
    </AppModal>
  );

  const renderConfirmDelete = () => (
    <AppModal
      show
      onClose={() => !deleting && setConfirmDelete(null)}
      size="md"
      maxWidth={420}
      centered
      closeButton={!deleting}
      showSceneToggle={false}
      backdrop="static"
      title={
        confirmDelete.isColorSet
          ? tr("BrandPanel.delete_color_set", "Delete color set")
          : i18n.t("delete_file_title") || "Dosyayı sil"
      }
      footer={
        <div className="app-modal__footer">
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            disabled={deleting}
            onClick={() => setConfirmDelete(null)}
          >
            {i18n.t("cancel") || "Vazgeç"}
          </button>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--danger"
            disabled={deleting}
            data-testid="brand-delete-confirm"
            onClick={removeAsset}
          >
            {i18n.t("delete") || "Sil"}
          </button>
        </div>
      }
    >
      <p className="brand-panel__confirm-text">
        {i18n.t("delete_file_confirm_prefix")}
        <b>{confirmDelete.name}</b>
        {i18n.t("delete_file_confirm_suffix")}
      </p>
    </AppModal>
  );

  const renderConfirmDeleteCategory = () => {
    const count = confirmDeleteCategory.assetCount || 0;
    return (
      <AppModal
        show
        onClose={() => !deleting && setConfirmDeleteCategory(null)}
        size="md"
        maxWidth={420}
        centered
        closeButton={!deleting}
        showSceneToggle={false}
        backdrop="static"
        title={tr("BrandPanel.delete_category", "Delete category")}
        footer={
          <div className="app-modal__footer">
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              disabled={deleting}
              onClick={() => setConfirmDeleteCategory(null)}
            >
              {i18n.t("cancel") || "Vazgeç"}
            </button>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--danger"
              disabled={deleting}
              data-testid="brand-category-delete-confirm"
              onClick={removeCategory}
            >
              {i18n.t("delete") || "Sil"}
            </button>
          </div>
        }
      >
        <p className="brand-panel__confirm-text">
          <b>{confirmDeleteCategory.name}</b>{" "}
          {tr("BrandPanel.delete_category_confirm", `and its ${count} files will be deleted.`, {
            count,
          })}
        </p>
      </AppModal>
    );
  };

  const renderBody = () => {
    if (openKey === COLORS) return renderColors();
    if (!kit) return error ? null : <LoadingRow />;
    const open = categoryOf(openKey);
    if (open) return renderCategory(open);
    return term ? renderSearchResults() : renderCategories();
  };

  return (
    <div className="brand-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm("")}
          placeholder={i18n.t("search") || "Ara"}
        />
        {onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            data-testid="brand-close"
            onClick={onClose}
            title={i18n.t("close") || "Kapat"}
          >
            <LuX size={15} />
          </button>
        )}
      </div>
      {error ? <div className="brand-panel__error">{error}</div> : null}
      <div className="brand-panel__scroll">{renderBody()}</div>
      {previewAsset ? renderPreview() : null}
      {confirmDelete ? renderConfirmDelete() : null}
      {confirmDeleteCategory ? renderConfirmDeleteCategory() : null}
    </div>
  );
};

const LoadingRow = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
    <LuLoaderCircle size={20} />
  </div>
);

BrandPanel.propTypes = {
  isClm: PropTypes.bool,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boxes: PropTypes.object.isRequired,
  pluginToolbars: PropTypes.object,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default BrandPanel;
